'use client'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Back link */}
        <Link 
          href="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to search
        </Link>

        {/* Header */}
        <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: November 2025</p>

        {/* Content */}
        <div className="prose prose-stone dark:prose-invert max-w-none space-y-8">
          
          {/* Service Description */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Service Description</h2>
            <p className="text-muted-foreground leading-relaxed">
              Roostr is a rental apartment search aggregator. We collect and display listings from 
              various third-party sources to help you find available rentals in one place. 
              We do not own, manage, lease, or have any control over the properties displayed on our platform.
            </p>
          </section>

          {/* No Guarantee */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. No Guarantee of Accuracy</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              While we strive to provide accurate and up-to-date information, we cannot guarantee 
              the accuracy, completeness, or availability of any listing. Specifically:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Prices, availability, and property details may change without notice</li>
              <li>AI-extracted data (such as price breakdowns and amenity detection) is estimated and may contain errors</li>
              <li>Photos and descriptions are provided by third parties and may not reflect current conditions</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Always verify all details directly with the landlord or listing agency before making any decisions.
            </p>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed">
              Some features, such as saving searches, require you to create an account. 
              You are responsible for maintaining the confidentiality of your account credentials 
              and for all activities that occur under your account. We reserve the right to 
              suspend or terminate accounts that violate these terms.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Roostr acts solely as an information aggregator and is not a party to any 
              transaction between you and property owners, landlords, or agencies. We are not liable for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Any disputes, losses, or damages arising from rental transactions</li>
              <li>Fraudulent listings or misrepresentations by third parties</li>
              <li>Your inability to find suitable accommodation</li>
              <li>Any decisions made based on information displayed on our platform</li>
            </ul>
          </section>

          {/* Listing Content */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Listing Content</h2>
            <p className="text-muted-foreground leading-relaxed">
              All listing content, including text, images, and property details, belongs to 
              the original sources and property owners. We display this content for informational 
              purposes only and make no claim of ownership over third-party materials.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms from time to time. Continued use of Roostr after
              changes are posted constitutes your acceptance of the revised terms. 
              We encourage you to review this page periodically.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these terms, please contact us at{' '}
              <a href="mailto:hello@roostr.me" className="text-primary hover:underline">
                hello@roostr.me
              </a>
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}

